import { useEffect, useState } from "react";
import {
    subscribeToStorageKey,
    watchStorageKey,
} from "../utils/localStorageEmitter";

export const LOCALSTORAGE_MEDIUM_CURRENT =
  "ilovemacumba:medium:current" as const;

// Register key for same-window change notifications
watchStorageKey(LOCALSTORAGE_MEDIUM_CURRENT);

export type CachedMedium = {
  id?: string;
  [k: string]: unknown;
};

function readMediumFromStorage(): CachedMedium | null | undefined {
  try {
    if (typeof window === "undefined" || !window.localStorage) return undefined;

    const raw = localStorage.getItem(LOCALSTORAGE_MEDIUM_CURRENT);
    if (!raw) return undefined;

    const parsed = JSON.parse(raw);
    // Handle explicit null
    if (parsed === null) return null;

    return parsed as CachedMedium;
  } catch {
    return undefined;
  }
}

/**
 * Read the cached medium id/object from LocalStorage.
 * - Reads the global `ilovemacumba:medium:current` key.
 * - Returns null when explicitly set to null
 * - Returns undefined when not set or on error
 *
 * This hook is reactive and updates when localStorage changes.
 */
export function useCachedMedium() {
  const [medium, setMedium] = useState<CachedMedium | null | undefined>(
    readMediumFromStorage,
  );

  useEffect(() => {
    // Event-driven: notified on same-window setItem AND cross-tab StorageEvent
    const unsubscribe = subscribeToStorageKey(
      LOCALSTORAGE_MEDIUM_CURRENT,
      () => setMedium(readMediumFromStorage()),
    );

    return unsubscribe;
  }, []);

  return medium;
}

export default useCachedMedium;
